import React, { useState, useContext, useMemo } from "react";
import { Button, Form, Table, Badge } from "react-bootstrap";
import bloqueosContext from "../../context/bloqueos/bloqueosContext";
import Swal from "sweetalert2";

const formatearFecha = (fecha) => {
  return new Date(fecha).toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getBadgeTipo = (tipo) => {
  const badges = {
    MANTENIMIENTO: "warning",
    EVENTO_PRIVADO: "info",
    CLIMA: "secondary",
    CLASES: "primary",
    OTRO: "dark",
  };
  return badges[tipo] || "dark";
};

const getBadgeEstado = (estado) => {
  const badges = {
    ACTIVO: "success",
    COMPLETADO: "primary",
    CANCELADO: "danger",
  };
  return badges[estado] || "secondary";
};

const BloqueosActivos = ({ onBloqueoEliminado }) => {
  const {
    bloqueosList,
    loading,
    error,
    obtenerBloqueosActivos,
    eliminarBloqueo,
    eliminarBloqueosMultiples,
  } = useContext(bloqueosContext);

  const [bloqueosSeleccionados, setBloqueosSeleccionados] = useState([]);
  // Por defecto solo mostramos los bloqueos vigentes (cuyo periodo no finalizó)
  const [soloVigentes, setSoloVigentes] = useState(true);

  // Set para lookups O(1) al renderizar cada fila (evita includes() O(n) por fila)
  const seleccionadosSet = useMemo(
    () => new Set(bloqueosSeleccionados),
    [bloqueosSeleccionados]
  );

  // Lista efectivamente renderizada: si soloVigentes está activo, filtra los
  // bloqueos cuyo periodo (fechaFin) ya pasó, dejando los de hoy en adelante.
  const bloqueosVisibles = useMemo(() => {
    if (!bloqueosList) return [];
    if (!soloVigentes) return bloqueosList;
    const inicioHoy = new Date();
    inicioHoy.setHours(0, 0, 0, 0);
    return bloqueosList.filter((b) => new Date(b.fechaFin) >= inicioHoy);
  }, [bloqueosList, soloVigentes]);

  const handleBloqueoToggle = (bloqueoID) => {
    if (bloqueosSeleccionados.includes(bloqueoID)) {
      setBloqueosSeleccionados(bloqueosSeleccionados.filter((id) => id !== bloqueoID));
    } else {
      setBloqueosSeleccionados([...bloqueosSeleccionados, bloqueoID]);
    }
  };

  const handleSelectAllBloqueos = () => {
    if (bloqueosSeleccionados.length === bloqueosVisibles.length) {
      setBloqueosSeleccionados([]);
    } else {
      setBloqueosSeleccionados(bloqueosVisibles.map((b) => b._id));
    }
  };

  const handleEliminar = async (bloqueoID) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará el bloqueo permanentemente y no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      customClass: {
        container: 'swal-high-z'
      }
    });

    if (result.isConfirmed) {
      try {
        await eliminarBloqueo(bloqueoID);
        obtenerBloqueosActivos();
        if (onBloqueoEliminado) {
          onBloqueoEliminado();
        }
      } catch (error) {
        console.error("Error al eliminar bloqueo:", error);
      }
    }
  };

  const handleEliminarSeleccionados = async () => {
    if (bloqueosSeleccionados.length === 0) return;

    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Se eliminarán ${bloqueosSeleccionados.length} bloqueo(s) permanentemente. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar todos",
      cancelButtonText: "Cancelar",
      customClass: {
        container: 'swal-high-z'
      }
    });

    if (result.isConfirmed) {
      try {
        await eliminarBloqueosMultiples(bloqueosSeleccionados);
        setBloqueosSeleccionados([]);
        obtenerBloqueosActivos();
        if (onBloqueoEliminado) {
          onBloqueoEliminado();
        }
      } catch (error) {
        console.error("Error al eliminar bloqueos:", error);
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h5 className="mb-0">Bloqueos</h5>
          <Form.Check
            type="checkbox"
            id="filtroBloqueosVigentes"
            label={soloVigentes ? "Bloqueos vigentes" : "Todos los bloqueos"}
            checked={soloVigentes}
            onChange={(e) => setSoloVigentes(e.target.checked)}
            className="mb-0"
          />
        </div>
        {bloqueosSeleccionados.length > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={handleEliminarSeleccionados}
          >
            🗑 Eliminar seleccionados ({bloqueosSeleccionados.length})
          </Button>
        )}
      </div>
      {loading && <p>Cargando...</p>}
      {error && <div className="alert alert-danger">{error}</div>}
      {bloqueosList && bloqueosVisibles.length === 0 && (
        <p className="text-muted">
          {soloVigentes ? "No hay bloqueos vigentes" : "No hay bloqueos activos"}
        </p>
      )}
      {bloqueosVisibles.length > 0 && (
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th style={{ width: "40px", textAlign: "center" }}>
                  <Form.Check
                    type="checkbox"
                    checked={bloqueosSeleccionados.length === bloqueosVisibles.length && bloqueosVisibles.length > 0}
                    onChange={handleSelectAllBloqueos}
                    title="Seleccionar todos"
                  />
                </th>
                <th>Cancha</th>
                <th>Periodo</th>
                <th>Horario</th>
                <th>Tipo</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {bloqueosVisibles.map((bloqueo) => (
                <tr key={bloqueo._id}>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <Form.Check
                      type="checkbox"
                      checked={seleccionadosSet.has(bloqueo._id)}
                      onChange={() => handleBloqueoToggle(bloqueo._id)}
                    />
                  </td>
                  <td>
                    <strong>{bloqueo.nameActivo}</strong>
                    <br />
                    <small className="text-muted">{bloqueo.codActivo}</small>
                  </td>
                  <td>
                    <small>
                      {formatearFecha(bloqueo.fechaInicio)}
                      <br />
                      {formatearFecha(bloqueo.fechaFin)}
                    </small>
                  </td>
                  <td>
                    {bloqueo.horaInicio ? (
                      <small>
                        {bloqueo.horaInicio} - {bloqueo.horaFin}
                      </small>
                    ) : (
                      <small className="text-muted">Día completo</small>
                    )}
                  </td>
                  <td>
                    <Badge bg={getBadgeTipo(bloqueo.tipo)}>
                      {bloqueo.tipo}
                    </Badge>
                  </td>
                  <td>
                    <small>{bloqueo.descripcion}</small>
                  </td>
                  <td>
                    <Badge bg={getBadgeEstado(bloqueo.estado)}>
                      {bloqueo.estado}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleEliminar(bloqueo._id)}
                      title="Eliminar bloqueo"
                    >
                      🗑
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default React.memo(BloqueosActivos);
